import axios from 'axios';

const api = axios.create({
  baseURL: '/shouyintai',
  timeout: 30000,
});

/**
 * 401 处理（v7.0.31.1 修复）：
 * - 收到 401 时先尝试用 refreshToken 调 /auth/refresh 换新 access token，
 *   成功后用新令牌重发原请求，用户无感知；
 * - 并发 401 通过单飞（single-flight）refreshPromise 去重，避免多次刷新；
 * - 刷新失败（或本地无 refreshToken）则清除凭证并跳转登录页（带 redirect 回跳参数），
 *   防止前端持续重试造成后端 401 日志刷屏。
 */
let refreshPromise: Promise<string> | null = null;

/**
 * R-07：会话代（session epoch）。
 *
 * 背景：静默刷新（refreshAccessToken）是异步的，收银员点「签退」时可能正好有一次
 * 刷新在途。刷新返回后如果照旧写回 localStorage 与默认 Authorization 头，旧账号的
 * 令牌就会在签退之后复活，下一位收银员开单会顶着上一个收银员的身份 —— 这正是
 * R-07 要消除的身份混用。
 *
 * 做法：每次登录 / 签退都递增该计数；异步刷新在开始时记下代值，写回前比对，
 * 代值已变化就丢弃结果。这样「签退前发起、签退后返回」的刷新只会失败，不会污染新会话。
 */
let sessionEpoch = 0;

/** 读取当前会话代（测试与调试用，业务代码一般不必直接调用） */
export function getSessionEpoch(): number {
  return sessionEpoch;
}

/** 递增会话代，作废所有在途的异步会话结果 */
export function bumpSessionEpoch(): number {
  return ++sessionEpoch;
}

/**
 * R-07：主动作废当前会话。
 * 递增会话代 + 清除本地凭证 + 摘掉默认鉴权头，供「签退」调用。
 */
export function invalidateSession(): void {
  bumpSessionEpoch();
  clearCredentials();
}

/** 会话在异步等待期间被签退/切换账号作废 */
const SESSION_INVALIDATED_MESSAGE = 'Session invalidated';

function getRefreshToken(): string | null {
  try {
    return localStorage.getItem('refreshToken');
  } catch {
    return null;
  }
}

function clearCredentials(): void {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentStoreId');
  } catch {
    /* localStorage 不可用时忽略 */
  }
  delete api.defaults.headers.common['Authorization'];
}

function clearCredentialsAndRedirect(): void {
  clearCredentials();
  const current = window.location.pathname + window.location.search;
  if (!current.startsWith('/login')) {
    window.location.href = '/login?redirect=' + encodeURIComponent(current);
  }
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  // R-07：记下发起刷新时的会话代，写回前比对，防止签退后旧令牌复活
  const epochAtStart = sessionEpoch;
  // 用独立的 axios 调用（不带默认 Authorization、不走本实例拦截器），避免递归
  const res = await axios.post(
    '/shouyintai/auth/refresh',
    { refreshToken },
    { timeout: 30000 }
  );
  // P1-2 兼容解包：后端 transform interceptor 会再包一层 { data: ... }，
  // 若接口走统一包装则取 res.data.data，未包装时回退 res.data
  const data = res.data?.data || res.data || {};
  if (!data.accessToken) {
    throw new Error('Refresh response missing accessToken');
  }
  // R-07：刷新期间发生了签退 / 换号，丢弃本次结果，绝不把旧账号令牌写回
  if (epochAtStart !== sessionEpoch) {
    throw new Error(SESSION_INVALIDATED_MESSAGE);
  }
  localStorage.setItem('token', data.accessToken);
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken);
  }
  api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
  return data.accessToken as string;
}

api.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    const status = err.response?.status;
    const original = err.config as (typeof err.config & { _retried?: boolean }) | undefined;
    const isAuthEndpoint = !!original?.url && original.url.includes('/auth/');
    // P1-2b：错误信息提取兼容多种后端错误结构
    const msg = err.response?.data?.message
      || err.response?.data?.data?.message
      || err.response?.data?.error
      || err.message
      || 'Request failed';

    // 401 且非认证接口本身、且未重试过 → 尝试刷新令牌后重发原请求
    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      try {
        const token = refreshPromise
          ? await refreshPromise
          : await (refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null;
            }));
        original._retried = true;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` };
        // 注意必须 return await：重发后若仍 401，需让 rejection 落入下方 catch
        //（裸 return promise 在 try 中不会触发 catch —— JS 语义陷阱）
        return await api.request(original);
      } catch (refreshErr: any) {
        // R-07：刷新在途时用户已签退 / 换号，本次结果按会话作废处理：
        // 不再写回、不再跳转（签退流程本身已把用户送到登录页），避免误报网络异常
        if (refreshErr?.message === SESSION_INVALIDATED_MESSAGE) {
          return Promise.reject(new Error('登录已过期，请重新登录'));
        }
        // P2-2：区分刷新失败的原因，避免网络抖动/后端 500 被误报为"登录已过期"
        const refreshStatus = refreshErr?.response?.status;
        const isAuthFailure =
          refreshStatus === 401
          || refreshErr?.message === 'No refresh token'
          || refreshErr?.message === 'Refresh response missing accessToken';
        if (isAuthFailure) {
          // refresh token 过期/无效：凭证已不可用，清除并跳登录
          clearCredentialsAndRedirect();
          return Promise.reject(new Error('登录已过期，请重新登录'));
        }
        // 网络异常（无 response）/ 后端 5xx：凭证大概率仍有效，不清除、不跳登录，
        // 仅让本次请求以准确消息失败，用户重试即可恢复
        const isServerError = typeof refreshStatus === 'number' && refreshStatus >= 500;
        return Promise.reject(
          new Error(isServerError ? '服务繁忙，请稍后重试' : '网络异常，请重试')
        );
      }
    }
    const rejection = new Error(msg);
    (rejection as any).response = err.response;
    return Promise.reject(rejection);
  }
);

/** Attach currentStoreId to params if set; empty values are omitted for backward compatibility */
export function withStoreId(params?: any): any {
  const storeId = localStorage.getItem('currentStoreId');
  if (!storeId) return params || {};
  return { ...params, storeId };
}

/** 解析 JWT 的 exp（秒级时间戳）；token 非 JWT 或解析失败返回 null */
function getTokenExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // base64url → base64 → UTF-8 安全解码
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const exp = JSON.parse(json).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

/**
 * P2-5：路由进入页面前预检 token 有效性，避免"页面已渲染、首个请求才 401"。
 * - 无 token → false
 * - token 未过期（预留 30s 时钟偏差缓冲）→ true
 * - token 已过期且有 refreshToken → 静默刷新（复用单飞去重），成功 true / 失败 false
 * - token 已过期且无 refreshToken → 清除凭证，false
 */
export async function ensureFreshToken(): Promise<boolean> {
  let token: string | null = null;
  try {
    token = localStorage.getItem('token');
  } catch {
    return false;
  }
  if (!token) return false;
  const exp = getTokenExp(token);
  if (exp === null || exp * 1000 > Date.now() + 30_000) return true;
  if (!getRefreshToken()) {
    clearCredentials();
    return false;
  }
  try {
    await (refreshPromise
      ? refreshPromise
      : (refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        })));
    return true;
  } catch {
    clearCredentials();
    return false;
  }
}

export default api;
